import { useState, useEffect } from 'react';
import {
  parseImportedSnippet,
  type ImportParseResult
} from '../../utils/importSnippetParser';

interface ImportSettingsModalProps {
  isOpen: boolean;
  currentPartnerKey: string;
  onClose: () => void;
  onImport: (result: ImportParseResult) => void;
}

type ModalView = 'input' | 'warnings' | 'mismatch-warning' | 'confirm';

export default function ImportSettingsModal({
  isOpen,
  currentPartnerKey,
  onClose,
  onImport
}: ImportSettingsModalProps) {
  const [view, setView] = useState<ModalView>('input');
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [parseResult, setParseResult] = useState<ImportParseResult | null>(
    null
  );

  useEffect(() => {
    if (isOpen) {
      setView('input');
      setInputText('');
      setError(undefined);
      setParseResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  function advanceAfterWarnings(result: ImportParseResult) {
    const hasMismatch =
      !!result.extractedPartnerKey &&
      result.extractedPartnerKey !== currentPartnerKey;
    setView(hasMismatch ? 'mismatch-warning' : 'confirm');
  }

  function handleParse() {
    const result = parseImportedSnippet(inputText);
    if (result.error) {
      setError(result.error);
      return;
    }
    setError(undefined);
    setParseResult(result);
    if (result.warnings.length > 0) {
      setView('warnings');
    } else {
      advanceAfterWarnings(result);
    }
  }

  const cancelButton = (
    <button
      type="button"
      onClick={onClose}
      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
    >
      Cancel
    </button>
  );

  if (view === 'input') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
          <h2 className="mb-2 text-lg font-semibold text-[#014876]">
            Import Settings
          </h2>
          <p className="mb-4 text-sm leading-6 text-gray-600">
            Paste your existing iframe snippet or src URL below to restore your
            previous configuration.
          </p>
          <label
            htmlFor="import-snippet-input"
            className="mb-1 block text-sm font-semibold text-[#014876]"
          >
            Paste your iframe snippet or src URL
          </label>
          <textarea
            id="import-snippet-input"
            value={inputText}
            onChange={e => {
              setInputText(e.target.value);
              if (error) setError(undefined);
            }}
            rows={6}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs leading-5 focus:border-[#014876] focus:outline-none"
            placeholder={
              '<iframe src="https://namebubbles.com/embed-config.html?product_tag=..." ...></iframe>'
            }
          />
          {error && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-3">
            {cancelButton}
            <button
              type="button"
              onClick={handleParse}
              className="rounded-lg bg-[#014876] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#013456]"
            >
              Import Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'warnings' && parseResult) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
          <h2 className="mb-2 text-lg font-semibold text-[#014876]">
            Import Settings
          </h2>
          <p className="mb-3 text-sm font-semibold text-amber-700">
            Some settings could not be fully parsed:
          </p>
          <ul className="mb-4 space-y-1">
            {parseResult.warnings.map((w, i) => (
              <li key={i} className="text-sm leading-5 text-amber-700">
                &bull; {w}
              </li>
            ))}
          </ul>
          <p className="mb-4 text-sm leading-6 text-gray-600">
            All other recognized settings will still be imported. Would you like
            to continue?
          </p>
          <div className="flex justify-end gap-3">
            {cancelButton}
            <button
              type="button"
              onClick={() => advanceAfterWarnings(parseResult)}
              className="rounded-lg bg-[#014876] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#013456]"
            >
              Continue anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'mismatch-warning' && parseResult) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
          <h2 className="mb-2 text-lg font-semibold text-[#014876]">
            Different Affiliate ID
          </h2>
          <p className="mb-6 text-sm leading-6 text-gray-600">
            This snippet was generated for a different affiliate ID.
            Affiliate-specific settings (such as product selection and tracking)
            will not transfer, but appearance and theme settings will. Would you
            like to proceed?
          </p>
          <div className="flex justify-end gap-3">
            {cancelButton}
            <button
              type="button"
              onClick={() => setView('confirm')}
              className="rounded-lg bg-[#014876] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#013456]"
            >
              Yes, import what I can
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'confirm' && parseResult) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
          <h2 className="mb-2 text-lg font-semibold text-[#014876]">
            Import Settings
          </h2>
          <p className="mb-6 text-sm leading-6 text-gray-600">
            This will replace your current configurator settings with the values
            from the pasted snippet. Continue?
          </p>
          <div className="flex justify-end gap-3">
            {cancelButton}
            <button
              type="button"
              onClick={() => onImport(parseResult)}
              className="rounded-lg bg-[#014876] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#013456]"
            >
              Yes, import settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
